import { useCallback, useRef } from "react";
import { useMutation } from "react-query";

import axios, { AxiosError } from "axios";
import { CLIENT_SYNC_SVC_ADDRESS } from "constants/app.constants";

import type { ClientSyncRequestPayload } from "./useClientSyncController";

// ─── Types ───────────────────────────────────────────────────────────────────

export type CancelSyncType = ClientSyncRequestPayload["syncType"];

interface CancelSyncErrorResponse {
  error: string;
  message: string;
}

export interface UseCancelSyncProps {
  /** Called on 204 — server confirmed cancellation. Tear down stream, show info toast. */
  onCancelled: (syncType: CancelSyncType) => void;
  /**
   * Called on 409 — the operation is already processing and cannot be cancelled.
   * Keep the loading state active; the SSE stream will deliver the terminal event.
   */
  onAlreadyProcessing: (syncType: CancelSyncType, message: string) => void;
  /** Called on 404 — the operation was not found; treat as already gone. */
  onNotFound: (syncType: CancelSyncType) => void;
  /** Called for network errors or unexpected HTTP status codes. */
  onError: (syncType: CancelSyncType, message: string) => void;
}

export interface UseCancelSyncReturn {
  /**
   * Sends DELETE /sync/{correlationId} for the given sync type.
   * No-ops if no correlationId is currently tracked for that type.
   */
  cancelSync: (syncType: CancelSyncType, correlationId: string) => void;
  /** True while any cancel request is in-flight. */
  isCancelling: boolean;
  /** True while a specific sync type's cancel is in-flight. */
  isCancellingType: (syncType: CancelSyncType) => boolean;
}

// ─── API ─────────────────────────────────────────────────────────────────────

const buildCancelSyncPath = (correlationId: string): string =>
  `${CLIENT_SYNC_SVC_ADDRESS}/sync/${encodeURIComponent(correlationId)}`;

const extractErrorMessage = (
  error: unknown,
  fallback: string
): { message: string; is409: boolean } => {
  if (!axios.isAxiosError(error)) {
    return {
      message: error instanceof Error ? error.message || fallback : fallback,
      is409: false
    };
  }

  const axiosError = error as AxiosError<CancelSyncErrorResponse>;
  const status = axiosError.response?.status;
  const data = axiosError.response?.data;

  const message =
    (typeof data?.message === "string" && data.message.length > 0
      ? data.message
      : undefined) ??
    (typeof data?.error === "string" && data.error.length > 0 ? data.error : undefined) ??
    axiosError.message ??
    fallback;

  return { message, is409: status === 409 };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useCancelSync = ({
  onCancelled,
  onAlreadyProcessing,
  onNotFound,
  onError
}: UseCancelSyncProps): UseCancelSyncReturn => {
  // Track which syncType is associated with each in-flight cancel mutation so
  // the onSuccess / onError callbacks can route to the correct handler.
  const pendingCancelTypeRef = useRef<Map<string, CancelSyncType>>(new Map());
  const inFlightTypesRef = useRef<Set<CancelSyncType>>(new Set());

  const cancelMutation = useMutation(
    async ({
      correlationId
    }: {
      correlationId: string;
      syncType: CancelSyncType;
    }): Promise<{ correlationId: string; wasNotFound: boolean }> => {
      let wasNotFound = false;

      try {
        await axios.delete(buildCancelSyncPath(correlationId));
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          wasNotFound = true;
        } else {
          throw error;
        }
      }

      return { correlationId, wasNotFound };
    },
    {
      onSuccess: ({ correlationId, wasNotFound }) => {
        const syncType = pendingCancelTypeRef.current.get(correlationId);
        pendingCancelTypeRef.current.delete(correlationId);

        if (!syncType) {
          return;
        }

        inFlightTypesRef.current.delete(syncType);

        if (wasNotFound) {
          onNotFound(syncType);
        } else {
          onCancelled(syncType);
        }
      },
      onError: (
        error: unknown,
        variables: { correlationId: string; syncType: CancelSyncType }
      ) => {
        const { correlationId, syncType } = variables;
        pendingCancelTypeRef.current.delete(correlationId);
        inFlightTypesRef.current.delete(syncType);

        const { message, is409 } = extractErrorMessage(
          error,
          "Failed to cancel sync operation."
        );

        if (is409) {
          onAlreadyProcessing(
            syncType,
            message ||
              "This sync operation is already processing and cannot be cancelled."
          );
        } else {
          onError(syncType, message);
        }
      }
    }
  );

  const cancelSync = useCallback(
    (syncType: CancelSyncType, correlationId: string) => {
      if (inFlightTypesRef.current.has(syncType)) {
        return;
      }

      inFlightTypesRef.current.add(syncType);
      pendingCancelTypeRef.current.set(correlationId, syncType);
      cancelMutation.mutate({ correlationId, syncType });
    },
    [cancelMutation]
  );

  const isCancellingType = useCallback(
    (syncType: CancelSyncType): boolean => inFlightTypesRef.current.has(syncType),
    // inFlightTypesRef is a ref so it doesn't trigger re-renders; callers that
    // need reactive updates should use `isCancelling` from the mutation instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cancelMutation.isLoading]
  );

  return {
    cancelSync,
    isCancelling: cancelMutation.isLoading,
    isCancellingType
  };
};
